import type { DataTagSummary } from '@robovision/data'
import {
  ImportExportIOJobStatus,
  type ImportExportRecordImportJobResponse,
} from '@robovision/import-export'


type ConstEnum<T, V = string> = Extract<T[keyof T], V>

export const ImportJobStatus = {
  ...ImportExportIOJobStatus,
  UPLOADING: 'UPLOADING',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ImportJobStatus = ConstEnum<typeof ImportJobStatus>

interface ImportJobParams {
  amountDone?: number
  amountFailed?: number
  amountTotal?: number
  createdAt?: Date
  dataFormat?: string
  datapoolId: string
  error?: string
  importId: string
  jobId?: string
  name: string
  status?: ImportJobStatus
  tagAttributes?: Record<string, string>
  tagId?: string
}

export class ImportJob {
  abortController?: AbortController
  amountDone?: number
  amountFailed?: number
  amountTotal?: number
  createdAt: Date
  dataFormat?: string
  datapoolId: string
  error?: string
  importId: string // an internal id to keep track of jobs, may or may not be the same as the backend job id
  jobId?: string
  name: string
  status: ImportJobStatus
  tagId?: string
  tagAttributes?: Record<string, string>
  uploadProgress?: number

  constructor (params: ImportJobParams) {
    this.amountDone = params.amountDone
    this.amountFailed = params.amountFailed
    this.amountTotal = params.amountTotal
    this.createdAt = params.createdAt ?? new Date()
    this.dataFormat = params.dataFormat
    this.datapoolId = params.datapoolId
    this.error = params.error
    this.importId = params.importId
    this.jobId = params.jobId
    this.name = params.name
    this.status = params.status ?? ImportJobStatus.UPLOADING
    this.tagAttributes = params.tagAttributes
    this.tagId = params.tagId
  }

  public get amountSuccess () {
    return (this.amountDone ?? 0) - (this.amountFailed ?? 0)
  }

  public get isFinished () {
    return this.status !== ImportJobStatus.RUNNING &&
    this.status !== ImportJobStatus.STARTING &&
    this.status !== ImportJobStatus.STOPPING &&
    this.status !== ImportJobStatus.UPLOADING
  }

  public get progress () {
    if (!this.amountTotal) return 0
    return (this.amountDone ?? 0) / this.amountTotal
  }

  public static fromResponse (
    data: ImportExportRecordImportJobResponse,
    tag: DataTagSummary,
    importId?: string,
  ) {
    const job = new ImportJob({
      amountDone: data.amountDone,
      amountFailed: data.amountFailed,
      amountTotal: data.amountTotal,
      createdAt: new Date(data.createdAt),
      dataFormat: data.dataFormat,
      datapoolId: data.datapoolId,
      importId: importId ?? data.jobId,
      jobId: data.jobId,
      name: tag.name,
      status: data.jobStatus,
      tagAttributes: data.tagMetadata?.attributes,
      tagId: data.tagId,
    })


    return job
  }
}
